import React from 'react';
import { Box, Container, Typography, Grid, Paper } from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

const FeatureSection = () => {
  const features = [
    {
      title: "Portfolio Analysis",
      description: "Get deep insights into your portfolio's dividend sustainability, growth potential, and risk factors. Our advanced algorithms analyze each holding's financial health and dividend history.",
      icon: AnalyticsIcon,
      imageSrc: "/api/placeholder/600/400",
      imageAlt: "Portfolio Analysis Dashboard"
    },
    {
      title: "Risk Assessment",
      description: "Understand your portfolio's risk exposure with our comprehensive risk metrics. We analyze sector concentration, dividend cut risks, and market sensitivity to help you make informed decisions.",
      icon: TimelineIcon,
      imageSrc: "/api/placeholder/600/400",
      imageAlt: "Risk Assessment Visualization"
    },
    {
      title: "Dividend Health Monitoring",
      description: "Track the dividend health of your investments with real-time monitoring of payout ratios, earnings coverage, and dividend growth trends. Stay ahead of potential dividend cuts.",
      icon: AccountBalanceIcon,
      imageSrc: "/api/placeholder/600/400",
      imageAlt: "Dividend Health Monitoring"
    },
    {
      title: "Improvement Recommendations",
      description: "Receive actionable recommendations to enhance your portfolio's performance. Our AI-driven insights suggest optimal position sizes, diversification opportunities, and dividend growth prospects.",
      icon: AssignmentTurnedInIcon,
      imageSrc: "/api/placeholder/600/400",
      imageAlt: "Portfolio Recommendations"
    }
  ];

  const FeatureRow = ({ feature, index }) => {
    const isEven = index % 2 === 0;
    const Icon = feature.icon;

    const ContentSection = () => (
      <Box sx={{ p: { xs: 3, md: 6 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 1,
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Typography variant="h4" fontWeight="bold">
            {feature.title}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontSize: '1.1rem', lineHeight: 1.7 }}>
          {feature.description}
        </Typography>
      </Box>
    );

    const ImageSection = () => (
      <Box
        sx={{
          height: { xs: 300, md: 400 },
          width: '100%',
          overflow: 'hidden',
          borderRadius: { xs: 2, md: isEven ? '0 8px 8px 0' : '8px 0 0 8px' },
        }}
      >
        <img
          src={feature.imageSrc}
          alt={feature.imageAlt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </Box>
    );

    return (
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          mb: 6,
          backgroundColor: 'background.paper',
        }}
      >
        <Grid container>
          {isEven ? (
            <>
              <Grid item xs={12} md={6}>
                <ContentSection />
              </Grid>
              <Grid item xs={12} md={6}>
                <ImageSection />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <ImageSection />
              </Grid>
              <Grid item xs={12} md={6}>
                <ContentSection />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    );
  };

  return (
    <Box
      id="featuresSection"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.paper',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 8,
            fontWeight: 'bold',
          }}
        >
          Key Features
        </Typography>

        <Box>
          {features.map((feature, index) => (
            <FeatureRow key={feature.title} feature={feature} index={index} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FeatureSection;